import { computed, ref, watch } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';

/**
 * Shared targeting sources for live-chat campaigns: the website inbox options
 * and the sender list that reloads whenever the selected inbox changes. Used
 * by both the legacy dialog form and the full-page builder so the two never
 * drift.
 *
 * @param {import('vue').Ref|Function} inboxIdRef Watch source for the
 *   selected inbox id.
 * @param {object} [options]
 * @param {Function} [options.onSenderLoadError] Called with the error when
 *   the sender list fails to load; the list is cleared either way.
 */
export function useLiveChatTargeting(inboxIdRef, { onSenderLoadError } = {}) {
  const store = useStore();
  const websiteInboxes = useMapGetter('inboxes/getWebsiteInboxes');
  const senderList = ref([]);

  const mapToOptions = items =>
    items?.map(item => ({ value: item.id, label: item.name })) ?? [];

  const inboxOptions = computed(() => mapToOptions(websiteInboxes.value));

  // `Bot` (value 0) is the historical sentinel for "no human sender".
  const senderOptions = computed(() => [
    { value: 0, label: 'Bot' },
    ...mapToOptions(senderList.value),
  ]);

  watch(
    inboxIdRef,
    async inboxId => {
      if (!inboxId) {
        senderList.value = [];
        return;
      }

      try {
        const response = await store.dispatch('inboxMembers/get', { inboxId });
        senderList.value = response?.data?.payload ?? [];
      } catch (error) {
        senderList.value = [];
        onSenderLoadError?.(error);
      }
    },
    { immediate: true }
  );

  return { inboxOptions, senderOptions };
}
